import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Check, Send, Loader } from 'lucide-react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "lagos",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch('/admin/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Request failed');
      setIsSubmitted(true);
    } catch {
      alert('Something went wrong — please email us directly at info@raha-iduu.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center"
      >
        <div className="w-20 h-20 border-2 border-[#C4A77D] rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="text-[#C4A77D]" size={40} />
        </div>
        <h3 className="text-3xl text-white mb-4" style={ { fontFamily: "'Playfair Display', serif" }}>Message Sent!</h3>
        <p className="text-gray-400 mb-8">
          Thank you for contacting us. We'll get back to you within 24 hours.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Contact Information */}
        <div>
          <h3 className="text-2xl text-[#C4A77D] mb-6" style={ { fontFamily: "'Playfair Display', serif" }}>Get in Touch</h3>
          <p className="text-gray-400 mb-8">
            Ready to experience premium concierge services? Contact us today and let us elevate your lifestyle.
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#C4A77D]/10 border border-[#C4A77D]/20 flex items-center justify-center flex-shrink-0">
                <Mail className="text-[#C4A77D]" size={20} />
              </div>
              <div>
                <h4 className="text-white font-light mb-1">Email</h4>
                <p className="text-gray-400">info@raha-iduu.com</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
              required
              className="col-span-2 sm:col-span-1 bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#C4A77D]/50"
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              required
              className="col-span-2 sm:col-span-1 bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#C4A77D]/50"
            />
          </div>
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number"
            value={formData.phone}
            onChange={handleChange}
            className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#C4A77D]/50"
          />
          <select
            name="city"
            value={formData.city}
            onChange={handleChange}
            className="w-full bg-[#0b1a14] border border-white/10 px-4 py-3 text-gray-400 text-sm focus:outline-none focus:border-[#C4A77D]/50"
          >
            <option value="lagos">Lagos</option>
            <option value="abuja">Abuja</option>
            <option value="calabar">Calabar</option>
          </select>
          <select
            name="service"
            value={formData.service}
            onChange={handleChange}
            className="w-full bg-[#0b1a14] border border-white/10 px-4 py-3 text-gray-400 text-sm focus:outline-none focus:border-[#C4A77D]/50"
          >
            <option value="">Select a Service</option>
            <option value="travel">Travel & Transport</option>
            <option value="events">Events</option>
            <option value="lifestyle">Lifestyle Management</option>
          </select>
          <textarea
            name="message"
            placeholder="Tell us about your requirements..."
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#C4A77D]/50 resize-none"
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-8 py-4 border border-[#C4A77D] bg-[#C4A77D] text-black text-xs uppercase tracking-wider transition-all duration-300 hover:bg-white hover:border-white flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <><Loader className="animate-spin" size={16} /> Sending...</>
            ) : (
              <><Send size={16} /> Send Message</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
